
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    role: '',
    phone: '',
    email: '',
    cityState: '',
    interest: '',
    message: '',
    requestTechSpec: false,
    requestCompliance: false,
    scheduleDemo: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      
      // Combine first and last name
      formBody.append('name', `${formData.firstName} ${formData.lastName}`.trim());
      formBody.append('company', formData.company);
      formBody.append('role', formData.role);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('cityState', formData.cityState);
      formBody.append('interest', formData.interest);
      
      // Build message with checkbox selections
      let message = formData.message;
      if (formData.requestTechSpec || formData.requestCompliance || formData.scheduleDemo) {
        message += '\n\nRequests:';
        if (formData.requestTechSpec) message += '\n- Technical Spec Sheet';
        if (formData.requestCompliance) message += '\n- Compliance Packet';
        if (formData.scheduleDemo) message += '\n- Schedule Facility Demo';
      }
      formBody.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d613ct8omoleh5thveg0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          company: '',
          role: '',
          phone: '',
          email: '',
          cityState: '',
          interest: '',
          message: '',
          requestTechSpec: false,
          requestCompliance: false,
          scheduleDemo: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-32">
        {/* Header */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h1 className="font-serif text-5xl md:text-6xl font-light text-gray-900 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl leading-relaxed">
              Get in touch with our team for quotes, product information, or any questions about our red light and infrared therapy solutions.
            </p>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="font-serif text-3xl font-light text-gray-900 mb-8">
                  Get In Touch
                </h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  We're here to help you find the perfect therapy solution for your needs. Whether you're looking for commercial equipment or residential wellness products, our team is ready to assist you.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Email</h3>
                      <a href="mailto:Info@NovaradianceDist.com" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                        Info@NovaradianceDist.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Business Hours</h3>
                      <p className="text-gray-700">Monday - Friday: 9:00 AM - 5:00 PM EST</p>
                      <p className="text-gray-700">Saturday - Sunday: Closed</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Location</h3>
                      <p className="text-gray-700">Serving customers across the United States</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 bg-gray-50 rounded-lg p-6">
                  <h3 className="font-serif text-xl font-medium text-gray-900 mb-3">
                    Why Work With Us?
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">US-based support in your time zone</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">No language barriers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">Expert product guidance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">Comprehensive warranty support</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="font-serif text-2xl font-medium text-gray-900 mb-6">
                  Send Us a Message
                </h2>

                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="space-y-5">
                    {/* First & Last Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-900 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-900 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Smith"
                        />
                      </div>
                    </div>

                    {/* Company / Facility */}
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">
                        Company / Facility *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="Your Company or Facility Name"
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-900 mb-2">
                        Role *
                      </label>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="">Select your role</option>
                        <option value="PT Director">PT Director</option>
                        <option value="Orthopedic Practice Admin">Orthopedic Practice Admin</option>
                        <option value="Gym Owner/Operator">Gym Owner/Operator</option>
                        <option value="Recovery Studio Owner">Recovery Studio Owner</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Email & Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* City/State */}
                    <div>
                      <label htmlFor="cityState" className="block text-sm font-medium text-gray-900 mb-2">
                        City, State *
                      </label>
                      <input
                        type="text"
                        id="cityState"
                        name="cityState"
                        value={formData.cityState}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="New York, NY"
                      />
                    </div>

                    {/* Interest */}
                    <div>
                      <label htmlFor="interest" className="block text-sm font-medium text-gray-900 mb-2">
                        Product Interest *
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="">Select your interest</option>
                        <option value="Beds">Therapy Beds</option>
                        <option value="Panels">Light Therapy Panels</option>
                        <option value="Masks">Facial Masks</option>
                        <option value="Multiple">Multiple Products</option>
                        <option value="Not sure">Not Sure</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                        placeholder="Tell us about your needs or ask any questions..."
                      ></textarea>
                      <div className="text-xs text-gray-500 mt-1">
                        {formData.message.length}/500 characters
                      </div>
                    </div>

                    {/* Checkboxes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-3">
                        Additional Requests (Optional)
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="requestTechSpec"
                            checked={formData.requestTechSpec}
                            onChange={handleChange}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Request Technical Spec Sheet</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="requestCompliance"
                            checked={formData.requestCompliance}
                            onChange={handleChange}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Request Compliance Packet</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="scheduleDemo"
                            checked={formData.scheduleDemo}
                            onChange={handleChange}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Schedule a Facility Demo</span>
                        </label>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-teal-600 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-800">
                        <i className="ri-check-line mr-2"></i>
                        Thank you! Your message has been sent successfully. We'll get back to you soon.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800">
                        <i className="ri-error-warning-line mr-2"></i>
                        Sorry, there was an error sending your message. Please try again or email us directly.
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
